import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { HelpOutline, QueueMusic, Save, Schedule, Settings, ShieldOutlined } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { usePermissions } from '../contexts/PermissionContext';
import { parseNotificationEmails } from '../utils/notificationEmails';
import WaveformPlayerField from './WaveformPlayerField';
import {
  DEFAULT_TENANT_OPERATING_HOURS,
  normalizeTenantOperatingHours,
  WEEKDAY_ORDER,
  type OperatingHoursMap,
  type Weekday,
} from '../utils/tenantOperatingHours';

type MusicSlot = 'queueWait' | 'agentHold';
type AccountTenantSettingsSection = 'all' | 'organization' | 'operatingHours' | 'music';

interface TenantSettingsPayload {
  id: string;
  name: string;
  email: string;
  notificationEmails: string[];
  queueWaitMusicFileId?: string | null;
  agentHoldMusicFileId?: string | null;
  queueWaitMusicPrepared?: boolean;
  agentHoldMusicPrepared?: boolean;
  queueWaitMusicError?: string | null;
  agentHoldMusicError?: string | null;
  operatingHours?: OperatingHoursMap;
  autoOperatorReadinessGuardEnabled?: boolean;
}

interface TenantMusicMedia {
  id: string;
  originalFilename: string;
  mimeType: string;
  fileSize: number;
  downloadUrl: string | null;
}

interface AccountTenantSettingsTabProps {
  section?: AccountTenantSettingsSection;
}

const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const size = bytes / 1024 ** exponent;
  return `${size.toFixed(exponent === 0 ? 0 : 2)} ${units[exponent]}`;
};

export default function AccountTenantSettingsTab({ section = 'all' }: AccountTenantSettingsTabProps) {
  const { t } = useTranslation(['pages', 'common']);
  const { hasPermission } = usePermissions();

  const canRead = hasPermission('tenant_settings_read');
  const canUpdate = hasPermission('tenant_settings_update');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [notificationEmailsInput, setNotificationEmailsInput] = useState('');
  const [notificationEmailsError, setNotificationEmailsError] = useState<string | null>(null);
  const [operatingHoursError, setOperatingHoursError] = useState<string | null>(null);
  const [uploadingMusicSlot, setUploadingMusicSlot] = useState<MusicSlot | null>(null);
  const [musicUploadProgress, setMusicUploadProgress] = useState<{
    queueWait: number | null;
    agentHold: number | null;
  }>({ queueWait: null, agentHold: null });

  const [formData, setFormData] = useState<TenantSettingsPayload>({
    id: '',
    name: '',
    email: '',
    notificationEmails: [],
    queueWaitMusicFileId: null,
    agentHoldMusicFileId: null,
    queueWaitMusicPrepared: false,
    agentHoldMusicPrepared: false,
    queueWaitMusicError: null,
    agentHoldMusicError: null,
    operatingHours: DEFAULT_TENANT_OPERATING_HOURS,
    autoOperatorReadinessGuardEnabled: true,
  });

  const [musicMedia, setMusicMedia] = useState<{
    queueWait: TenantMusicMedia | null;
    agentHold: TenantMusicMedia | null;
  }>({ queueWait: null, agentHold: null });

  const validateNotificationEmails = (value: string) => {
    const emails = parseNotificationEmails(value);
    if (emails.length === 0) {
      return { emails: [], error: null };
    }
    const invalidEmails = emails.filter((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    if (invalidEmails.length > 0) {
      return {
        emails,
        error: t('pages:tenants.notificationEmailsInvalid', { count: invalidEmails.length }),
      };
    }
    return { emails, error: null };
  };

  const getDayLabel = (day: Weekday): string => {
    const labels: Record<Weekday, string> = {
      mon: t('common:days.monday'),
      tue: t('common:days.tuesday'),
      wed: t('common:days.wednesday'),
      thu: t('common:days.thursday'),
      fri: t('common:days.friday'),
      sat: t('common:days.saturday'),
      sun: t('common:days.sunday'),
    };
    return labels[day];
  };

  /**
   * Validate organization operating hours before saving tenant settings.
   * Enabled 24h days intentionally bypass start/end requirements.
   */
  const validateOperatingHours = (hours: OperatingHoursMap): string | null => {
    for (const day of WEEKDAY_ORDER) {
      const config = hours[day];
      if (!config.enabled || config.allDay) {
        continue;
      }
      if (!config.start || !config.end) {
        return t('pages:tenants.operatingHoursIncomplete', { day: getDayLabel(day) });
      }
      if (config.start >= config.end) {
        return t('pages:tenants.operatingHoursInvalidRange', { day: getDayLabel(day) });
      }
    }
    return null;
  };

  const fetchMusicMedia = useCallback(async (fileId: string | null | undefined): Promise<TenantMusicMedia | null> => {
    if (!fileId) {
      return null;
    }
    try {
      const response = await api.get(`/api/media/${fileId}/`);
      return {
        id: response.data?.id,
        originalFilename: response.data?.originalFilename || response.data?.original_filename || '-',
        mimeType: response.data?.mimeType || response.data?.mime_type || 'application/octet-stream',
        fileSize: response.data?.fileSize || response.data?.file_size || 0,
        downloadUrl: response.data?.downloadUrl || response.data?.download_url || null,
      };
    } catch (fetchError) {
      console.error('Error loading media metadata:', fetchError);
      return null;
    }
  }, []);

  const loadTenantSettings = useCallback(async () => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await api.get('/api/account/tenant-settings/');
      const tenant = response.data as TenantSettingsPayload;
      setFormData({
        id: tenant.id,
        name: tenant.name || '',
        email: tenant.email || '',
        notificationEmails: tenant.notificationEmails || [],
        queueWaitMusicFileId: tenant.queueWaitMusicFileId ?? null,
        agentHoldMusicFileId: tenant.agentHoldMusicFileId ?? null,
        queueWaitMusicPrepared: Boolean(tenant.queueWaitMusicPrepared),
        agentHoldMusicPrepared: Boolean(tenant.agentHoldMusicPrepared),
        queueWaitMusicError: tenant.queueWaitMusicError ?? null,
        agentHoldMusicError: tenant.agentHoldMusicError ?? null,
        operatingHours: normalizeTenantOperatingHours(tenant.operatingHours),
        autoOperatorReadinessGuardEnabled: tenant.autoOperatorReadinessGuardEnabled ?? true,
      });
      setNotificationEmailsInput((tenant.notificationEmails || []).join(', '));
      setError(null);
    } catch (loadError) {
      console.error('Error loading account tenant settings:', loadError);
      setError(t('pages:account.settings.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [canRead, t]);

  useEffect(() => {
    void loadTenantSettings();
  }, [loadTenantSettings]);

  useEffect(() => {
    let cancelled = false;
    const loadMusicMedia = async () => {
      const [queueWait, agentHold] = await Promise.all([
        fetchMusicMedia(formData.queueWaitMusicFileId),
        fetchMusicMedia(formData.agentHoldMusicFileId),
      ]);
      if (!cancelled) {
        setMusicMedia({ queueWait, agentHold });
      }
    };

    if (!canRead) {
      setMusicMedia({ queueWait: null, agentHold: null });
      return;
    }

    void loadMusicMedia();
    return () => {
      cancelled = true;
    };
  }, [canRead, formData.queueWaitMusicFileId, formData.agentHoldMusicFileId, fetchMusicMedia]);

  const handleNotificationEmailsChange = (value: string) => {
    setNotificationEmailsInput(value);
    const { emails, error: validationError } = validateNotificationEmails(value);
    setNotificationEmailsError(validationError);
    setFormData((prev) => ({ ...prev, notificationEmails: emails }));
  };

  const handleOperatingHourToggle = (day: Weekday) => {
    setFormData((prev) => {
      const currentHours = normalizeTenantOperatingHours(prev.operatingHours);
      const currentDay = currentHours[day];
      const nextEnabled = !currentDay.enabled;
      return {
        ...prev,
        operatingHours: {
          ...currentHours,
          [day]: {
            ...currentDay,
            enabled: nextEnabled,
            allDay: nextEnabled ? currentDay.allDay : false,
            start: nextEnabled ? currentDay.start ?? DEFAULT_TENANT_OPERATING_HOURS[day].start : null,
            end: nextEnabled ? currentDay.end ?? DEFAULT_TENANT_OPERATING_HOURS[day].end : null,
          },
        },
      };
    });
    setOperatingHoursError(null);
  };

  const handleOperatingHourAllDayToggle = (day: Weekday) => {
    setFormData((prev) => {
      const currentHours = normalizeTenantOperatingHours(prev.operatingHours);
      const currentDay = currentHours[day];
      const nextAllDay = !currentDay.allDay;
      return {
        ...prev,
        operatingHours: {
          ...currentHours,
          [day]: {
            ...currentDay,
            allDay: nextAllDay,
            start: nextAllDay ? null : currentDay.start ?? DEFAULT_TENANT_OPERATING_HOURS[day].start,
            end: nextAllDay ? null : currentDay.end ?? DEFAULT_TENANT_OPERATING_HOURS[day].end,
          },
        },
      };
    });
    setOperatingHoursError(null);
  };

  const handleOperatingHourTimeChange = (day: Weekday, field: 'start' | 'end', value: string) => {
    setFormData((prev) => {
      const currentHours = normalizeTenantOperatingHours(prev.operatingHours);
      return {
        ...prev,
        operatingHours: {
          ...currentHours,
          [day]: {
            ...currentHours[day],
            [field]: value || null,
          },
        },
      };
    });
    setOperatingHoursError(null);
  };

  const handleSave = async () => {
    if (!canUpdate) {
      return;
    }
    const shouldSaveOrganization = section === 'all' || section === 'organization';
    const shouldSaveOperatingHours = section === 'all' || section === 'operatingHours';

    if (shouldSaveOrganization) {
      const { error: validationError } = validateNotificationEmails(notificationEmailsInput);
      if (validationError) {
        setNotificationEmailsError(validationError);
        return;
      }
    }
    const normalizedOperatingHours = normalizeTenantOperatingHours(formData.operatingHours);
    if (shouldSaveOperatingHours) {
      const operatingValidationError = validateOperatingHours(normalizedOperatingHours);
      if (operatingValidationError) {
        setOperatingHoursError(operatingValidationError);
        return;
      }
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload: Partial<TenantSettingsPayload> = {};
      if (shouldSaveOrganization) {
        payload.name = formData.name;
        payload.email = formData.email;
        payload.notificationEmails = formData.notificationEmails;
      }
      if (shouldSaveOperatingHours) {
        payload.operatingHours = normalizedOperatingHours;
        payload.autoOperatorReadinessGuardEnabled = Boolean(formData.autoOperatorReadinessGuardEnabled);
      }
      await api.patch('/api/account/tenant-settings/', payload);
      await loadTenantSettings();
      setSuccess(t('pages:account.settings.success'));
    } catch (saveError) {
      console.error('Error saving account tenant settings:', saveError);
      setError(t('pages:account.settings.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const handleMusicFileUpload = async (slot: MusicSlot, file: File | null) => {
    if (!file || !canUpdate) {
      return;
    }

    setUploadingMusicSlot(slot);
    setMusicUploadProgress((prev) => ({ ...prev, [slot]: 0 }));
    setError(null);
    setSuccess(null);

    try {
      const uploadPayload = new FormData();
      uploadPayload.append('file', file);
      uploadPayload.append('file_type', 'other');
      uploadPayload.append(
        'metadata',
        JSON.stringify({
          category: 'tenant_music_on_hold',
          slot,
          source: 'account_settings',
        })
      );
      const uploadResponse = await api.post('/api/media/upload/', uploadPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event) => {
          if (!event.total || event.total <= 0) {
            setMusicUploadProgress((prev) => ({ ...prev, [slot]: null }));
            return;
          }
          const progressValue = (event.loaded / event.total) * 100;
          setMusicUploadProgress((prev) => ({ ...prev, [slot]: progressValue }));
        },
      });

      const uploadedMediaId = uploadResponse.data?.id;
      if (!uploadedMediaId) {
        throw new Error('Media upload did not return id');
      }

      const patchPayload =
        slot === 'queueWait'
          ? { queueWaitMusicFileId: uploadedMediaId }
          : { agentHoldMusicFileId: uploadedMediaId };
      await api.patch('/api/account/tenant-settings/', patchPayload);
      await loadTenantSettings();
      setSuccess(t('pages:tenants.musicOnHold.uploadSuccess'));
    } catch (uploadError) {
      console.error('Error uploading tenant music file:', uploadError);
      setError(t('pages:tenants.musicOnHold.errors.uploadFailed'));
    } finally {
      setUploadingMusicSlot(null);
      setMusicUploadProgress((prev) => ({ ...prev, [slot]: null }));
    }
  };

  const handleMusicFileRemove = async (slot: MusicSlot) => {
    if (!canUpdate) {
      return;
    }

    setUploadingMusicSlot(slot);
    setMusicUploadProgress((prev) => ({ ...prev, [slot]: null }));
    setError(null);
    setSuccess(null);

    try {
      const patchPayload =
        slot === 'queueWait'
          ? { queueWaitMusicFileId: null }
          : { agentHoldMusicFileId: null };
      await api.patch('/api/account/tenant-settings/', patchPayload);
      await loadTenantSettings();
      setSuccess(t('pages:tenants.musicOnHold.removeSuccess'));
    } catch (removeError) {
      console.error('Error removing tenant music file:', removeError);
      setError(t('pages:tenants.musicOnHold.errors.removeFailed'));
    } finally {
      setUploadingMusicSlot(null);
      setMusicUploadProgress((prev) => ({ ...prev, [slot]: null }));
    }
  };

  const getMusicStatusTone = (prepared: boolean, slotError: string | null): 'success' | 'error' | 'warning' => {
    if (slotError) {
      return 'error';
    }
    if (prepared) {
      return 'success';
    }
    return 'warning';
  };

  const getMusicStatusLabel = (slot: MusicSlot): string => {
    const isPrepared = slot === 'queueWait' ? formData.queueWaitMusicPrepared : formData.agentHoldMusicPrepared;
    const slotError = slot === 'queueWait' ? formData.queueWaitMusicError : formData.agentHoldMusicError;
    if (slotError) {
      return t('pages:tenants.musicOnHold.status.error');
    }
    if (isPrepared) {
      return t('pages:tenants.musicOnHold.status.ready');
    }
    return t('pages:tenants.musicOnHold.status.preparing');
  };

  const hasMusicFile = (slot: MusicSlot): boolean => {
    return slot === 'queueWait'
      ? Boolean(formData.queueWaitMusicFileId)
      : Boolean(formData.agentHoldMusicFileId);
  };

  const operatingHours = normalizeTenantOperatingHours(formData.operatingHours);

  if (!canRead) {
    return <Alert severity="warning">{t('pages:account.settings.accessDenied')}</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {(section === 'all' || section === 'organization') && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Settings color="primary" />
              <Typography variant="h6">{t('pages:account.settings.title')}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('pages:account.settings.subtitle')}
            </Typography>

            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {!canUpdate && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('pages:account.settings.readOnlyHint')}
              </Alert>
            )}

            <Stack spacing={2}>
              <TextField
                label={t('pages:tenants.name')}
                value={formData.name}
                onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
                required
                disabled={!canUpdate}
              />
              <TextField
                label={t('pages:tenants.email')}
                type="email"
                value={formData.email}
                onChange={(event) => setFormData((prev) => ({ ...prev, email: event.target.value }))}
                required
                disabled={!canUpdate}
              />
              <TextField
                label={
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography component="span">{t('pages:tenants.notificationEmails')}</Typography>
                    <Tooltip title={t('pages:tenants.notificationEmailsTooltip')} arrow>
                      <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                }
                value={notificationEmailsInput}
                onChange={(event) => handleNotificationEmailsChange(event.target.value)}
                onBlur={(event) => handleNotificationEmailsChange(event.target.value)}
                placeholder={t('pages:tenants.notificationEmailsPlaceholder')}
                helperText={notificationEmailsError || t('pages:tenants.notificationEmailsHelper')}
                error={Boolean(notificationEmailsError)}
                multiline
                minRows={2}
                disabled={!canUpdate}
              />

              <Box>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                  disabled={!canUpdate || saving}
                  onClick={handleSave}
                >
                  {saving ? t('common:actions.saving') : t('common:actions.save')}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {(section === 'all' || section === 'operatingHours') && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Schedule color="primary" />
              <Typography variant="h6">{t('pages:tenants.operatingHoursTitle')}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('pages:tenants.operatingHoursDesc')}
            </Typography>

            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {!canUpdate && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('pages:account.settings.readOnlyHint')}
              </Alert>
            )}

            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(formData.autoOperatorReadinessGuardEnabled)}
                        disabled={!canUpdate}
                        onChange={(event) => {
                          setFormData((prev) => ({
                            ...prev,
                            autoOperatorReadinessGuardEnabled: event.target.checked,
                          }));
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <ShieldOutlined fontSize="small" color="primary" />
                        <Typography component="span" fontWeight={700}>
                          {t('pages:tenants.operatorReadinessGuard.label')}
                        </Typography>
                        <Tooltip title={t('pages:tenants.operatorReadinessGuard.tooltip')} arrow>
                          <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                    }
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('pages:tenants.operatorReadinessGuard.description')}
                  </Typography>
                </Stack>
              </Paper>

              {operatingHoursError && <Alert severity="error">{operatingHoursError}</Alert>}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                {WEEKDAY_ORDER.map((day) => {
                  const dayConfig = operatingHours[day];
                  return (
                    <Paper
                      key={day}
                      variant="outlined"
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                        bgcolor: dayConfig.enabled ? 'background.paper' : 'action.hover',
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={dayConfig.enabled}
                            disabled={!canUpdate}
                            onChange={() => handleOperatingHourToggle(day)}
                          />
                        }
                        label={
                          <Typography
                            fontWeight={600}
                            sx={{
                              minWidth: 110,
                              color: dayConfig.enabled ? 'text.primary' : 'text.disabled',
                            }}
                          >
                            {getDayLabel(day)}
                          </Typography>
                        }
                        sx={{ mr: 1 }}
                      />
                      {dayConfig.enabled ? (
                        <>
                          {dayConfig.allDay ? (
                            <Chip
                              size="small"
                              color="success"
                              variant="outlined"
                              label={t('pages:tenants.operatingHoursAllDayBadge')}
                            />
                          ) : (
                            <>
                              <TimePicker
                                label={t('pages:tenants.operatingHoursStart')}
                                value={dayConfig.start ? dayjs(`2000-01-01T${dayConfig.start}`) : null}
                                onChange={(value: Dayjs | null) => {
                                  handleOperatingHourTimeChange(day, 'start', value ? value.format('HH:mm') : '');
                                }}
                                ampm={false}
                                disabled={!canUpdate}
                                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                              />
                              <Typography color="text.secondary">—</Typography>
                              <TimePicker
                                label={t('pages:tenants.operatingHoursEnd')}
                                value={dayConfig.end ? dayjs(`2000-01-01T${dayConfig.end}`) : null}
                                onChange={(value: Dayjs | null) => {
                                  handleOperatingHourTimeChange(day, 'end', value ? value.format('HH:mm') : '');
                                }}
                                ampm={false}
                                disabled={!canUpdate}
                                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                              />
                            </>
                          )}
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(dayConfig.allDay)}
                                disabled={!canUpdate}
                                onChange={() => handleOperatingHourAllDayToggle(day)}
                              />
                            }
                            label={t('pages:tenants.operatingHoursAllDay')}
                          />
                        </>
                      ) : (
                        <Typography color="text.disabled" sx={{ fontStyle: 'italic' }}>
                          {t('pages:tenants.operatingHoursClosed')}
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </LocalizationProvider>

              <Box>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                  disabled={!canUpdate || saving}
                  onClick={handleSave}
                >
                  {saving ? t('common:actions.saving') : t('common:actions.save')}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {(section === 'all' || section === 'music') && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <QueueMusic color="primary" />
              <Typography variant="h6">{t('pages:tenants.musicOnHold.title')}</Typography>
            </Box>

            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {!canUpdate && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('pages:account.settings.readOnlyHint')}
              </Alert>
            )}

            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {t('pages:tenants.musicOnHold.queueWait.title')}
                </Typography>
                {hasMusicFile('queueWait') && (
                  <Chip
                    size="small"
                    color={getMusicStatusTone(Boolean(formData.queueWaitMusicPrepared), formData.queueWaitMusicError ?? null)}
                    label={getMusicStatusLabel('queueWait')}
                  />
                )}
              </Box>
              <WaveformPlayerField
                url={musicMedia.queueWait?.downloadUrl || undefined}
                displayName={musicMedia.queueWait?.originalFilename}
                downloadFilename={musicMedia.queueWait?.originalFilename}
                isUploading={uploadingMusicSlot === 'queueWait'}
                uploadProgress={musicUploadProgress.queueWait}
                uploadEnabled={canUpdate}
                onSelectFile={(selectedFile) => handleMusicFileUpload('queueWait', selectedFile)}
                onRemove={() => handleMusicFileRemove('queueWait')}
              />
              {hasMusicFile('queueWait') && formData.queueWaitMusicError && (
                <Alert severity="error">{formData.queueWaitMusicError}</Alert>
              )}
              {musicMedia.queueWait?.fileSize ? (
                <Typography variant="caption" color="text.secondary">
                  {t('pages:tenants.musicOnHold.fileSize', { size: formatFileSize(musicMedia.queueWait.fileSize) })}
                </Typography>
              ) : null}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {t('pages:tenants.musicOnHold.agentHold.title')}
                </Typography>
                {hasMusicFile('agentHold') && (
                  <Chip
                    size="small"
                    color={getMusicStatusTone(Boolean(formData.agentHoldMusicPrepared), formData.agentHoldMusicError ?? null)}
                    label={getMusicStatusLabel('agentHold')}
                  />
                )}
              </Box>
              <WaveformPlayerField
                url={musicMedia.agentHold?.downloadUrl || undefined}
                displayName={musicMedia.agentHold?.originalFilename}
                downloadFilename={musicMedia.agentHold?.originalFilename}
                isUploading={uploadingMusicSlot === 'agentHold'}
                uploadProgress={musicUploadProgress.agentHold}
                uploadEnabled={canUpdate}
                onSelectFile={(selectedFile) => handleMusicFileUpload('agentHold', selectedFile)}
                onRemove={() => handleMusicFileRemove('agentHold')}
              />
              {hasMusicFile('agentHold') && formData.agentHoldMusicError && (
                <Alert severity="error">{formData.agentHoldMusicError}</Alert>
              )}
              {musicMedia.agentHold?.fileSize ? (
                <Typography variant="caption" color="text.secondary">
                  {t('pages:tenants.musicOnHold.fileSize', { size: formatFileSize(musicMedia.agentHold.fileSize) })}
                </Typography>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
